import React, { useState } from "react";
import ".././styles/DashboardScreen.css";
import { useSelector } from "react-redux";
import {
  Typography,
  Card,
  Box,
  Avatar,
  Chip,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
} from "@mui/material";
import {
  TrendingUp,
  TrendingDown,
  AlertCircle,
  Users,
  ShoppingBag,
  ArrowRight,
  CreditCard,
  Calendar,
  BarChart2,
  Truck,
  Clock,
  CheckCircle,
  XCircle,
  Layers,
  Percent,
  DollarSign,
  BarChart,
  TrendingUp as TrendingUpIcon,
  UserPlus,
  FileQuestion,
} from "lucide-react";
import {
  CircularProgressbarWithChildren,
  buildStyles,
} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import DashboardHeader from "./DashboardHeader";
import SalesChart from "./SalesChart";
import OrdersChart from "./OrdersChart";

// Quick links data
const quickLinks = [
  {
    title: "Create Order",
    icon: ShoppingBag,
    color: "#5652B0",
    link: "/orders/createOrder",
  },
  {
    title: "Add Customer",
    icon: Users,
    color: "#22c55e",
    link: "/customer/addcustomer",
  },
  {
    title: "Add Receipts",
    icon: CreditCard,
    color: "#f59e0b",
    link: "/receipt/addreceipt",
  },
  { title: "Add Voucher", icon: Calendar, color: "#F0C274", link: "/voucher" },
];

// Recent orders table data
const recentOrders = [
  {
    id: "#RS8721",
    customer: "Sharma Wholesale",
    date: "12 Dec 2023",
    amount: 24500,
    status: "completed",
  },
  {
    id: "#RS8734",
    customer: "Singh Enterprises",
    date: "11 Dec 2023",
    amount: 31000,
    status: "pending",
  },
  {
    id: "#RS8756",
    customer: "Gupta Traders",
    date: "10 Dec 2023",
    amount: 9200,
    status: "completed",
  },
  {
    id: "#RS8791",
    customer: "Jain Provisions",
    date: "9 Dec 2023",
    amount: 18700,
    status: "failed",
  },
];

// Inventory status data
const lowInventoryItems = [
  {
    name: "Rice (Basmati Premium)",
    currentStock: 15,
    minStock: 50,
    image: "https://via.placeholder.com/40",
  },
  {
    name: "Wheat Flour",
    currentStock: 8,
    minStock: 30,
    image: "https://via.placeholder.com/40",
  },
  {
    name: "Sugar (Premium)",
    currentStock: 12,
    minStock: 25,
    image: "https://via.placeholder.com/40",
  },
];

// Performance metrics
const performanceMetrics = [
  {
    title: "Total Revenue",
    value: "₹42,50,101",
    change: "+12%",
    isPositive: true,
    color: "#5652B0",
    icon: CreditCard,
  },
  {
    title: "Outstanding Balances",
    value: "₹53,00,989",
    change: "+8%",
    isPositive: false,
    color: "#E96479",
    icon: AlertCircle,
  },
  {
    title: "Order Fulfillment",
    value: "93%",
    change: "+5%",
    isPositive: true,
    color: "#22c55e",
    icon: Percent,
  },
  {
    title: "Total Orders",
    value: "1,002",
    change: "+18%",
    isPositive: true,
    color: "#F0C274",
    icon: Layers,
  },
];

// Order metrics data
const orderMetrics = [
  {
    title: "Pending Orders",
    value: "12",
    color: "#f59e0b",
    icon: Clock,
    link: "/orders",
  },
  {
    title: "Total Customers",
    value: "385",
    color: "#5652B0",
    icon: Users,
    link: "/customer/viewcustomer",
  },
  {
    title: "Pending Customer Requests",
    value: "8",
    color: "#E96479",
    icon: FileQuestion,
    link: "/customer/request",
  },
];

// Style for order status
const getStatusStyle = status => {
  switch (status) {
    case "completed":
      return {
        icon: <CheckCircle size={14} />,
        color: "#22c55e",
        bgColor: "rgba(34, 197, 94, 0.1)",
        text: "Completed",
      };
    case "pending":
      return {
        icon: <Clock size={14} />,
        color: "#f59e0b",
        bgColor: "rgba(245, 158, 11, 0.1)",
        text: "Pending",
      };
    case "failed":
      return {
        icon: <XCircle size={14} />,
        color: "#d32f2f",
        bgColor: "rgba(211, 47, 47, 0.1)",
        text: "Failed",
      };
    default:
      return {
        icon: null,
        color: "#6b7280",
        bgColor: "rgba(107, 114, 128, 0.1)",
        text: status,
      };
  }
};

const cardStyle = {
  border: "1px solid rgba(229, 231, 235, 0.8)",
  borderRadius: "12px",
  boxShadow: "none",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    borderColor: "rgba(86, 82, 176, 0.2)",
    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.03)",
  },
};

const DashboardComponent = () => {
  const sidebar = useSelector(state => state.sidebar);
  const [timeRange, setTimeRange] = useState("monthly");

  return (
    <div className="bg-[#F9FAFB] min-h-screen">
      <DashboardHeader />

      <div className="p-6">
        {/* Performance Metrics */}
        <Box className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {performanceMetrics.map((metric, index) => {
            const Icon = metric.icon;
            return (
              <Card
                key={index}
                elevation={0}
                sx={{
                  border: "1px solid",
                  borderColor: "rgba(229, 231, 235, 0.8)",
                  borderRadius: "12px",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: `${metric.color}40`,
                    boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.03)",
                    transform: "translateY(-2px)",
                  },
                }}>
                <Box className="p-4">
                  <Box className="flex items-start justify-between mb-3">
                    <Typography className="text-sm font-medium text-[#6B7280]">
                      {metric.title}
                    </Typography>
                    <Box
                      className="w-8 h-8 rounded-full flex items-center justify-center"
                      sx={{ backgroundColor: `${metric.color}15` }}>
                      <Icon size={16} color={metric.color} />
                    </Box>
                  </Box>

                  <Typography
                    variant="h6"
                    className="font-semibold text-[#374151] mb-1">
                    {metric.value}
                  </Typography>

                  <Box className="flex items-center gap-1.5">
                    {metric.isPositive ? (
                      <TrendingUp size={14} color="#22c55e" />
                    ) : (
                      <TrendingDown size={14} color="#E96479" />
                    )}
                    <Typography
                      className={`text-xs font-medium ${
                        metric.isPositive ? "text-[#22c55e]" : "text-[#E96479]"
                      }`}>
                      {metric.change} from last month
                    </Typography>
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>

        {/* Order Metrics */}
        <Box className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          {orderMetrics.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card
                key={index}
                elevation={0}
                sx={{
                  border: "1px solid rgba(229, 231, 235, 0.8)",
                  borderRadius: "12px",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    borderColor: `${stat.color}40`,
                    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.02)",
                  },
                }}>
                <Box className="p-4 flex items-center justify-between">
                  <Box>
                    <Typography className="text-xs font-medium text-[#6B7280] mb-1">
                      {stat.title}
                    </Typography>
                    <Box className="flex items-center">
                      <Typography
                        variant="h6"
                        className="font-semibold text-[#374151]">
                        {stat.value}
                      </Typography>
                    </Box>
                  </Box>
                  <Button
                    href={stat.link}
                    sx={{
                      minWidth: "auto",
                      width: "32px",
                      height: "32px",
                      borderRadius: "50%",
                      padding: 0,
                      backgroundColor: `${stat.color}15`,
                      color: stat.color,
                      "&:hover": {
                        backgroundColor: `${stat.color}30`,
                      },
                    }}>
                    <Icon size={16} />
                  </Button>
                </Box>
              </Card>
            );
          })}
        </Box>

        {/* Charts Section */}
        <Box className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <Card elevation={0} sx={cardStyle}>
            <SalesChart />
          </Card>
          <Card elevation={0} sx={cardStyle}>
            <OrdersChart />
          </Card>
        </Box>

        {/* Quick Links */}
        <Box className="mb-6">
          <Card elevation={0} sx={cardStyle}>
            <Box className="p-5">
              <Typography
                variant="subtitle1"
                className="font-medium text-[#374151] mb-4">
                Quick Actions
              </Typography>
              <Box className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {quickLinks.map((link, index) => {
                  const Icon = link.icon;
                  return (
                    <Button
                      key={index}
                      variant="text"
                      href={link.link}
                      className="flex flex-col items-center justify-center rounded-xl py-4 hover:bg-[rgba(86,82,176,0.04)] transition-all"
                      sx={{
                        color: "#4B5563",
                        "&:hover": {
                          backgroundColor: "rgba(86, 82, 176, 0.04)",
                        },
                      }}>
                      <Box
                        className="w-10 h-10 rounded-full flex items-center justify-center mb-2"
                        sx={{ backgroundColor: `${link.color}15` }}>
                        <Icon size={18} color={link.color} />
                      </Box>
                      <Typography className="text-xs font-medium text-[#374151]">
                        {link.title}
                      </Typography>
                    </Button>
                  );
                })}
              </Box>
            </Box>
          </Card>
        </Box>

        {/* Recent Orders and Low Inventory */}
        <Box className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent Orders */}
          <Card elevation={0} sx={cardStyle} className="lg:col-span-2">
            <Box className="p-5">
              <Box className="flex justify-between items-center mb-4">
                <Typography
                  variant="subtitle1"
                  className="font-medium text-[#374151]">
                  Recent Orders
                </Typography>
                <Button
                  href="/orders"
                  endIcon={<ArrowRight size={16} />}
                  className="text-xs text-[#5652B0] hover:text-[#4A47A3]"
                  sx={{
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}>
                  View All
                </Button>
              </Box>

              <TableContainer sx={{ maxHeight: 280 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell className="text-[#6B7280] font-medium text-xs">
                        Order ID
                      </TableCell>
                      <TableCell className="text-[#6B7280] font-medium text-xs">
                        Customer
                      </TableCell>
                      <TableCell className="text-[#6B7280] font-medium text-xs">
                        Date
                      </TableCell>
                      <TableCell className="text-[#6B7280] font-medium text-xs">
                        Amount
                      </TableCell>
                      <TableCell className="text-[#6B7280] font-medium text-xs">
                        Status
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recentOrders.map(order => {
                      const status = getStatusStyle(order.status);
                      return (
                        <TableRow
                          key={order.id}
                          hover
                          sx={{
                            "& td": {
                              paddingTop: "10px",
                              paddingBottom: "10px",
                              borderColor: "rgba(229, 231, 235, 0.5)",
                            },
                            "&:hover": {
                              backgroundColor: "rgba(249, 250, 251, 0.7)",
                            },
                          }}>
                          <TableCell className="font-medium text-xs">
                            {order.id}
                          </TableCell>
                          <TableCell className="text-xs">
                            {order.customer}
                          </TableCell>
                          <TableCell className="text-xs">
                            {order.date}
                          </TableCell>
                          <TableCell className="text-xs">
                            ₹{order.amount.toLocaleString("en-IN")}
                          </TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              icon={status.icon}
                              label={status.text}
                              sx={{
                                height: 22,
                                fontSize: "0.65rem",
                                fontWeight: 500,
                                backgroundColor: status.bgColor,
                                color: status.color,
                                "& .MuiChip-icon": {
                                  color: status.color,
                                  marginLeft: "4px",
                                  marginRight: "-2px",
                                },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Card>

          {/* Low Inventory Alert */}
          <Card elevation={0} sx={cardStyle}>
            <Box className="p-5">
              <Box className="flex justify-between items-center mb-4">
                <Typography
                  variant="subtitle1"
                  className="font-medium text-[#374151]">
                  Low Inventory Alert
                </Typography>
                <Button
                  href="/products"
                  endIcon={<ArrowRight size={14} />}
                  className="text-xs text-[#5652B0] hover:text-[#4A47A3]"
                  sx={{
                    textTransform: "none",
                    "&:hover": {
                      backgroundColor: "transparent",
                    },
                  }}>
                  View All
                </Button>
              </Box>

              <Box className="space-y-3">
                {lowInventoryItems.map((item, index) => (
                  <Box
                    key={index}
                    className="p-3 rounded-lg"
                    sx={{
                      backgroundColor: "rgba(254, 226, 226, 0.5)",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor: "rgba(254, 226, 226, 0.7)",
                      },
                    }}>
                    <Box className="flex items-center gap-3 mb-2">
                      <Avatar
                        src={item.image}
                        alt={item.name}
                        variant="rounded"
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "8px",
                          bgcolor: "rgba(211, 47, 47, 0.08)",
                        }}
                      />
                      <Box>
                        <Typography className="text-xs font-medium text-[#374151]">
                          {item.name}
                        </Typography>
                        <Typography className="text-[11px] text-[#E96479]">
                          Low stock alert
                        </Typography>
                      </Box>
                    </Box>

                    <Box className="mt-2">
                      <Box className="flex justify-between mb-1">
                        <Typography className="text-[11px] text-[#6B7280]">
                          Current: {item.currentStock} units
                        </Typography>
                        <Typography className="text-[11px] text-[#6B7280]">
                          Minimum: {item.minStock} units
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(item.currentStock / item.minStock) * 100}
                        sx={{
                          height: 4,
                          borderRadius: 2,
                          backgroundColor: "rgba(211, 47, 47, 0.08)",
                          "& .MuiLinearProgress-bar": {
                            backgroundColor: "#E96479",
                            borderRadius: 2,
                          },
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Card>
        </Box>
      </div>
    </div>
  );
};

export default DashboardComponent;
